/**
 * @description 全局弹窗store
 */

import {create} from 'zustand';
import {trimAllProjectTransactionsToFirstPage} from '@/hooks/points/useProjectTransactions';
import type {ProjectId} from '@/types';
import type {MembershipModalSourceType} from '@/commerce/modals/MembershipModal/types';
import type {PointsRechargeModalSourceType} from '@/commerce/modals/PointsRechargeModal/types';

export type PointsModalPayload =
    | {type: 'personal'}
    | {type: 'project'; projectId: ProjectId; projectName: string};

interface ModalState {
    loginModalOpen: boolean;
    toggleLoginModal: (open: boolean) => void;
    logoutConfirmOpen: boolean;
    toggleLogoutConfirm: (open: boolean) => void;
    pointsModalPayload: PointsModalPayload | null;
    openPointsModal: (payload: PointsModalPayload) => void;
    closePointsModal: () => void;
    /** 积分充值弹窗开关 */
    pointsRechargeModalOpen: boolean;
    togglePointsRechargeModal: (open: boolean) => void;
    /** 积分充值弹窗打开来源（scene_key） */
    pointsRechargeModalSource: PointsRechargeModalSourceType | null;
    setPointsRechargeModalSource: (source: PointsRechargeModalSourceType | null) => void;

    /** 成员订阅弹窗开关 */
    membershipModalOpen: boolean;
    setMembershipModalOpen: (open: boolean) => void;
    /** 成员订阅弹窗渠道来源 */
    membershipModalSource: MembershipModalSourceType | null;
    setMembershipModalSource: (source: MembershipModalSourceType | null) => void;
    /** 积分不足提示弹窗开关 */
    pointsInsufficientModalOpen: boolean;
    /** 触发积分不足弹窗的场景 key */
    pointsInsufficientSceneKey: string | null;
    openPointsInsufficientModal: (sceneKey: string) => void;
    closePointsInsufficientModal: () => void;
}

export const useModalStore = create<ModalState>((set) => ({
    // 登录弹窗
    loginModalOpen: false,
    toggleLoginModal: (open) => set({loginModalOpen: open}),
    // 登出确认弹窗
    logoutConfirmOpen: false,
    toggleLogoutConfirm: (open) => set({logoutConfirmOpen: open}),
    // 积分明细弹窗
    pointsModalPayload: null,
    openPointsModal: (payload) => set({pointsModalPayload: payload}),
    closePointsModal: () => {
        // 先卸载弹窗内容，避免 trim 后表格仍挂载且滚在底部误触发 fetchNextPage
        set({pointsModalPayload: null});
        trimAllProjectTransactionsToFirstPage();
    },
    // 积分充值弹窗
    pointsRechargeModalOpen: false,
    togglePointsRechargeModal: (open) => set({pointsRechargeModalOpen: open}),
    pointsRechargeModalSource: null,
    setPointsRechargeModalSource: (source) => set({pointsRechargeModalSource: source}),
    // 成员订阅弹窗
    membershipModalOpen: false,
    setMembershipModalOpen: (open) => set({membershipModalOpen: open}),
    membershipModalSource: null,
    setMembershipModalSource: (source) => set({membershipModalSource: source}),
    // 积分不足提示弹窗
    pointsInsufficientModalOpen: false,
    pointsInsufficientSceneKey: null,
    openPointsInsufficientModal: (sceneKey) =>
        set((state) =>
            state.pointsInsufficientModalOpen
                ? state
                : {pointsInsufficientModalOpen: true, pointsInsufficientSceneKey: sceneKey},
        ),
    closePointsInsufficientModal: () =>
        set({pointsInsufficientModalOpen: false, pointsInsufficientSceneKey: null}),
}));
